import { ProductKey } from "../types/billing";

/**
 * A product page's unfinished input, kept in this tab's sessionStorage across the trip to
 * Stripe, so that a buyer who left a run half set up to buy the Credits for it comes back to
 * the run as they left it.
 *
 * Written just before the browser leaves for Stripe, and only for the product the checkout was
 * started from. Read back, and removed, by that product's page the next time it opens. One
 * entry per product, each one naming its product inside as well as in its key, so a draft is
 * never poured into another product's page.
 *
 * What goes in is up to each page and must survive JSON: text, selections, company lists, and
 * the ids of files that already sit on a backend. A file the browser holds only as a `File`
 * cannot make the trip; a page keeps its name, to ask for it again.
 *
 * sessionStorage rather than localStorage: the draft belongs to the tab that went to Stripe, and
 * it goes when that tab does.
 */

const KEY_PREFIX = "iiq.productDraft.";

/**
 * Past this a draft is dropped rather than restored. Long enough for a checkout, a slow webhook
 * and a coffee; short enough that a page opened much later starts clean instead of reviving a
 * run the buyer has long since stopped thinking about.
 */
export const DRAFT_MAX_AGE_MS = 3 * 60 * 60 * 1000;

interface StoredDraft {
  product: ProductKey;
  savedAt: number;
  data: unknown;
}

const keyFor = (product: ProductKey): string => `${KEY_PREFIX}${product}`;

export const clearProductDraft = (product: ProductKey): void => {
  try {
    sessionStorage.removeItem(keyFor(product));
  } catch {
    // Nothing to clear where storage cannot be reached.
  }
};

/** Keep `data` as `product`'s draft, replacing any earlier one. */
export const saveProductDraft = (
  product: ProductKey,
  data: unknown,
  now: number = Date.now(),
): void => {
  const draft: StoredDraft = { product, savedAt: now, data };
  try {
    sessionStorage.setItem(keyFor(product), JSON.stringify(draft));
  } catch {
    // Storage can be full or switched off. The purchase goes ahead either way; what is lost is
    // the page's input, which the buyer can type again.
  }
};

/**
 * `product`'s draft, or `null` when there is none, it does not read, it names another product,
 * or it is too old. One that is found wanting is removed, so it is not looked at again.
 */
export const readProductDraft = (
  product: ProductKey,
  now: number = Date.now(),
): unknown => {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(keyFor(product));
  } catch {
    return null;
  }
  if (!raw) return null;

  let draft: Partial<StoredDraft> | null = null;
  try {
    draft = JSON.parse(raw) as Partial<StoredDraft> | null;
  } catch {
    draft = null;
  }
  const age =
    draft && typeof draft.savedAt === "number" ? now - draft.savedAt : NaN;
  // A negative age is a clock that moved back: nothing says how old the draft really is.
  const usable =
    draft !== null &&
    draft.product === product &&
    age >= 0 &&
    age <= DRAFT_MAX_AGE_MS &&
    draft.data !== undefined &&
    draft.data !== null;
  if (!usable) {
    clearProductDraft(product);
    return null;
  }
  return (draft as StoredDraft).data;
};

export const hasProductDraft = (
  product: ProductKey,
  now: number = Date.now(),
): boolean => readProductDraft(product, now) !== null;

/** Read the draft and remove it, so it is restored once and only once. */
export const takeProductDraft = (
  product: ProductKey,
  now: number = Date.now(),
): unknown => {
  const data = readProductDraft(product, now);
  clearProductDraft(product);
  return data;
};

/* ------------------------------------------------------------ reading a draft back */

/*
 * Small readers for the pages' own draft shapes. What comes back out of storage is only as
 * trustworthy as anything else in the tab, so each field is checked before a page uses it.
 */

export const draftString = (value: unknown, fallback = ""): string =>
  typeof value === "string" ? value : fallback;

export const draftStrings = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];

export const isDraftObject = (
  value: unknown,
): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);
